import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import {
  CalendarDaysIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  IdentificationIcon,
  PlusIcon,
  TrashIcon,
  TruckIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import PageLayout from '../../components/PageLayout';
import LoadingSpinner from '../../components/LoadingSpinner';
import EntityDrawer from '../../components/ui/EntityDrawer';
import Tabs from '../../components/ui/Tabs';
import { adminAPI } from '../../services/api';

type DriverDrawerTab = 'profile' | 'credentials' | 'availability' | 'blocks' | 'assignments';

type DriverForm = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  status: string;
  employmentType: 'full_time' | 'part_time' | 'contractor';
  homeBase: string;
  hourlyRate: string;
  licenseNumber: string;
  licenseExpiry: string;
  medicalCardExpiry: string;
  backgroundCheckExpiry: string;
  vehicleTypeQualifications: string[];
  notes: string;
  weeklyAvailability: Array<{
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    active: boolean;
  }>;
};

const vehicleTypes = ['sedan', 'suv', 'van', 'sprinter', 'minibus', 'motorcoach', 'truck'];
const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const defaultWeeklyAvailability = dayLabels.map((_, dayOfWeek) => ({
  dayOfWeek,
  startTime: '08:00',
  endTime: '18:00',
  active: dayOfWeek >= 1 && dayOfWeek <= 5,
}));

function labelize(value?: string | null) {
  return String(value || 'not set')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function emptyDriverForm(): DriverForm {
  return {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    status: 'active',
    employmentType: 'full_time',
    homeBase: '',
    hourlyRate: '0.00',
    licenseNumber: '',
    licenseExpiry: '',
    medicalCardExpiry: '',
    backgroundCheckExpiry: '',
    vehicleTypeQualifications: [],
    notes: '',
    weeklyAvailability: defaultWeeklyAvailability,
  };
}

function formFromDriver(driver: any): DriverForm {
  const availability = Array.isArray(driver?.weeklyAvailability) && driver.weeklyAvailability.length
    ? driver.weeklyAvailability
    : defaultWeeklyAvailability;
  return {
    firstName: driver?.firstName || '',
    lastName: driver?.lastName || '',
    email: driver?.email || '',
    phone: driver?.phone || '',
    status: driver?.status || 'active',
    employmentType: driver?.employmentType || 'full_time',
    homeBase: driver?.homeBase || '',
    hourlyRate: driver?.hourlyRate || '0.00',
    licenseNumber: driver?.licenseNumber || '',
    licenseExpiry: driver?.licenseExpiry || '',
    medicalCardExpiry: driver?.medicalCardExpiry || '',
    backgroundCheckExpiry: driver?.backgroundCheckExpiry || '',
    vehicleTypeQualifications: Array.isArray(driver?.vehicleTypeQualifications)
      ? driver.vehicleTypeQualifications
      : [],
    notes: driver?.notes || '',
    weeklyAvailability: dayLabels.map((_, dayOfWeek) => {
      const row = availability.find((entry: any) => Number(entry.dayOfWeek) === dayOfWeek);
      return {
        dayOfWeek,
        startTime: row?.startTime || '08:00',
        endTime: row?.endTime || '18:00',
        active: row?.active !== false && Boolean(row),
      };
    }),
  };
}

function statusClass(status?: string) {
  switch (status) {
    case 'active':
      return 'bg-green-100 text-green-800';
    case 'on_leave':
      return 'bg-yellow-100 text-yellow-800';
    case 'inactive':
      return 'bg-surface-container-low text-on-surface-variant';
    default:
      return 'bg-surface-container-low text-on-surface-variant';
  }
}

function AdminDrivers() {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    employmentType: '',
    qualification: '',
    page: 1,
    limit: 50,
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState<DriverDrawerTab>('profile');
  const [selectedDriverId, setSelectedDriverId] = useState<string | null>(null);
  const [form, setForm] = useState<DriverForm>(() => emptyDriverForm());
  const [blockForm, setBlockForm] = useState({
    startAt: '',
    endAt: '',
    blockType: 'time_off',
    notes: '',
  });

  const driversQuery = useQuery(
    ['admin-drivers', filters],
    () => adminAPI.getAllDrivers({
      search: filters.search || undefined,
      status: filters.status || undefined,
      employmentType: filters.employmentType || undefined,
      qualification: filters.qualification || undefined,
      page: filters.page,
      limit: filters.limit,
    }),
    {
      select: (response) => response.data,
      keepPreviousData: true,
    },
  );

  const selectedDriverQuery = useQuery(
    ['admin-driver', selectedDriverId],
    () => adminAPI.getDriver(selectedDriverId!),
    {
      enabled: Boolean(selectedDriverId),
      select: (response) => response.data,
      onSuccess: (driver) => setForm(formFromDriver(driver)),
    },
  );

  const scheduleQuery = useQuery(
    ['admin-driver-schedule', selectedDriverId],
    () => adminAPI.getDriverSchedule(selectedDriverId!),
    {
      enabled: Boolean(selectedDriverId && drawerOpen),
      select: (response) => response.data,
    },
  );

  const saveDriverMutation = useMutation(
    (payload: DriverForm) => {
      const compacted = {
        ...payload,
        email: payload.email || null,
        phone: payload.phone || null,
        homeBase: payload.homeBase || null,
        licenseNumber: payload.licenseNumber || null,
        licenseExpiry: payload.licenseExpiry || null,
        medicalCardExpiry: payload.medicalCardExpiry || null,
        backgroundCheckExpiry: payload.backgroundCheckExpiry || null,
        notes: payload.notes || null,
      };
      return selectedDriverId
        ? adminAPI.updateDriver(selectedDriverId, compacted)
        : adminAPI.createDriver(compacted);
    },
    {
      onSuccess: (response) => {
        toast.success(selectedDriverId ? 'Driver updated' : 'Driver created');
        const driver = response.data;
        setSelectedDriverId(driver.id);
        setForm(formFromDriver(driver));
        queryClient.invalidateQueries('admin-drivers');
        queryClient.invalidateQueries(['admin-driver', driver.id]);
        queryClient.invalidateQueries(['admin-driver-schedule', driver.id]);
      },
      onError: () => {
        toast.error('Unable to save driver');
      },
    },
  );

  const deleteDriverMutation = useMutation(
    (driverId: string) => adminAPI.deleteDriver(driverId),
    {
      onSuccess: () => {
        toast.success('Driver deleted');
        setDrawerOpen(false);
        setSelectedDriverId(null);
        queryClient.invalidateQueries('admin-drivers');
      },
      onError: () => {
        toast.error('Unable to delete driver');
      },
    },
  );

  const createBlockMutation = useMutation(
    () => adminAPI.createDriverScheduleBlock(selectedDriverId!, {
      startAt: blockForm.startAt,
      endAt: blockForm.endAt,
      blockType: blockForm.blockType,
      notes: blockForm.notes || null,
    }),
    {
      onSuccess: () => {
        toast.success('Schedule block added');
        setBlockForm({ startAt: '', endAt: '', blockType: 'time_off', notes: '' });
        queryClient.invalidateQueries(['admin-driver-schedule', selectedDriverId]);
      },
      onError: () => {
        toast.error('Unable to add schedule block');
      },
    },
  );

  const deleteBlockMutation = useMutation(
    (blockId: string) => adminAPI.deleteDriverScheduleBlock(selectedDriverId!, blockId),
    {
      onSuccess: () => {
        toast.success('Schedule block removed');
        queryClient.invalidateQueries(['admin-driver-schedule', selectedDriverId]);
      },
      onError: () => {
        toast.error('Unable to remove schedule block');
      },
    },
  );

  const drivers = useMemo(
    () => driversQuery.data?.data || driversQuery.data?.drivers || [],
    [driversQuery.data?.data, driversQuery.data?.drivers],
  );
  const summary = useMemo(
    () => driversQuery.data?.summary || {},
    [driversQuery.data?.summary],
  );
  const pagination = driversQuery.data?.pagination;
  const selectedDriver = selectedDriverQuery.data;
  const schedule = scheduleQuery.data;

  const derivedSummary = useMemo(() => ({
    total: summary.total ?? drivers.length,
    active: summary.active ?? drivers.filter((driver: any) => driver.status === 'active').length,
    inactive: summary.inactive ?? drivers.filter((driver: any) => driver.status !== 'active').length,
    credentialWarnings:
      summary.credentialWarnings ??
      drivers.filter((driver: any) => Array.isArray(driver.credentialWarnings) && driver.credentialWarnings.length).length,
  }), [drivers, summary]);

  useEffect(() => {
    if (!drawerOpen) {
      setDrawerTab('profile');
    }
  }, [drawerOpen]);

  function openCreateDrawer() {
    setSelectedDriverId(null);
    setForm(emptyDriverForm());
    setDrawerTab('profile');
    setDrawerOpen(true);
  }

  function openDriverDrawer(driver: any) {
    setSelectedDriverId(driver.id);
    setForm(formFromDriver(driver));
    setDrawerTab('profile');
    setDrawerOpen(true);
  }

  function updateAvailability(dayOfWeek: number, updates: Partial<DriverForm['weeklyAvailability'][number]>) {
    setForm((current) => ({
      ...current,
      weeklyAvailability: current.weeklyAvailability.map((entry) =>
        entry.dayOfWeek === dayOfWeek ? { ...entry, ...updates } : entry,
      ),
    }));
  }

  function toggleQualification(type: string) {
    setForm((current) => {
      const present = current.vehicleTypeQualifications.includes(type);
      return {
        ...current,
        vehicleTypeQualifications: present
          ? current.vehicleTypeQualifications.filter((entry) => entry !== type)
          : [...current.vehicleTypeQualifications, type],
      };
    });
  }

  return (
    <PageLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-on-surface-variant">Operations</p>
            <h1 className="mt-1 text-3xl font-bold text-on-surface">Drivers</h1>
            <p className="mt-2 max-w-3xl text-sm text-on-surface-variant">
              Manage driver readiness, scheduling exceptions, qualifications, and reservation assignments.
            </p>
          </div>
          <button onClick={openCreateDrawer} className="btn-primary inline-flex items-center gap-2">
            <PlusIcon className="h-5 w-5" />
            Add Driver
          </button>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <SummaryCard icon={<UserIcon />} label="Total drivers" value={derivedSummary.total} />
          <SummaryCard icon={<CheckCircleIcon />} label="Active" value={derivedSummary.active} />
          <SummaryCard icon={<ClockIcon />} label="Inactive / leave" value={derivedSummary.inactive} />
          <SummaryCard icon={<ExclamationTriangleIcon />} label="Credential review" value={derivedSummary.credentialWarnings} />
        </div>

        <div className="rounded-lg border border-outline bg-surface-container-lowest p-4">
          <div className="grid gap-3 md:grid-cols-[minmax(14rem,1fr)_12rem_12rem_12rem_auto] md:items-end">
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant">Search</span>
              <input
                className="input-field mt-1"
                placeholder="Name, email, phone, license"
                value={filters.search}
                onChange={(event) => setFilters({ ...filters, search: event.target.value, page: 1 })}
              />
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant">Status</span>
              <select
                className="input-field mt-1"
                value={filters.status}
                onChange={(event) => setFilters({ ...filters, status: event.target.value, page: 1 })}
              >
                <option value="">All statuses</option>
                <option value="active">Active</option>
                <option value="on_leave">On leave</option>
                <option value="inactive">Inactive</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant">Employment</span>
              <select
                className="input-field mt-1"
                value={filters.employmentType}
                onChange={(event) => setFilters({ ...filters, employmentType: event.target.value, page: 1 })}
              >
                <option value="">All types</option>
                <option value="full_time">Full time</option>
                <option value="part_time">Part time</option>
                <option value="contractor">Contractor</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant">Qualification</span>
              <select
                className="input-field mt-1"
                value={filters.qualification}
                onChange={(event) => setFilters({ ...filters, qualification: event.target.value, page: 1 })}
              >
                <option value="">Any vehicle</option>
                {vehicleTypes.map((type) => (
                  <option key={type} value={type}>{labelize(type)}</option>
                ))}
              </select>
            </label>
            <button
              className="btn-secondary"
              onClick={() => setFilters({ search: '', status: '', employmentType: '', qualification: '', page: 1, limit: 50 })}
            >
              Clear
            </button>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-outline bg-surface-container-lowest">
          <div className="overflow-x-auto">
            <table className="admin-table-grid min-w-[64rem]">
              <thead className="admin-table-head">
                <tr>
                  <th>Driver</th>
                  <th>Contact</th>
                  <th>Status</th>
                  <th>Credentials</th>
                  <th>Qualifications</th>
                  <th>Schedule</th>
                  <th className="text-right">Rate</th>
                </tr>
              </thead>
              <tbody className="admin-table-body">
                {driversQuery.isLoading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <tr key={index} className="admin-table-row animate-pulse">
                      <td colSpan={7}><div className="h-8 rounded bg-surface-container-low" /></td>
                    </tr>
                  ))
                ) : drivers.length ? (
                  drivers.map((driver: any) => (
                    <tr
                      key={driver.id}
                      className="admin-table-row cursor-pointer align-top"
                      onClick={() => openDriverDrawer(driver)}
                    >
                      <td>
                        <div className="font-semibold text-on-surface">{driver.name}</div>
                        <div className="text-xs text-on-surface-variant">{driver.homeBase || 'No home base'}</div>
                      </td>
                      <td>
                        <div className="text-sm text-on-surface">{driver.email || 'No email'}</div>
                        <div className="text-xs text-on-surface-variant">{driver.phone || 'No phone'}</div>
                      </td>
                      <td>
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${statusClass(driver.status)}`}>
                          {labelize(driver.status)}
                        </span>
                        <div className="mt-1 text-xs text-on-surface-variant">{labelize(driver.employmentType)}</div>
                      </td>
                      <td>
                        <div className="text-sm text-on-surface">{driver.licenseNumber || 'License not recorded'}</div>
                        <div className="text-xs text-on-surface-variant">
                          Exp {driver.licenseExpiry || 'not set'}
                        </div>
                        {driver.credentialWarnings?.length ? (
                          <div className="mt-1 text-xs font-medium text-yellow-700">
                            {driver.credentialWarnings.join(', ')}
                          </div>
                        ) : null}
                      </td>
                      <td>
                        <div className="flex max-w-xs flex-wrap gap-1">
                          {(driver.vehicleTypeQualifications || []).length ? (
                            driver.vehicleTypeQualifications.map((type: string) => (
                              <span key={type} className="rounded-full bg-surface-container-low px-2 py-0.5 text-xs text-on-surface-variant">
                                {labelize(type)}
                              </span>
                            ))
                          ) : (
                            <span className="text-xs text-on-surface-variant">No qualifications</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="text-sm text-on-surface">
                          {(driver.weeklyAvailability || []).filter((entry: any) => entry.active).length} active days
                        </div>
                        <div className="text-xs text-on-surface-variant">
                          {(driver.scheduleBlocks || []).length} blocks
                        </div>
                      </td>
                      <td className="text-right font-semibold text-on-surface">
                        ${Number(driver.hourlyRate || 0).toFixed(2)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-sm text-on-surface-variant">
                      No drivers match these filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-end gap-2">
            <button
              className="btn-secondary"
              disabled={filters.page <= 1}
              onClick={() => setFilters((current) => ({ ...current, page: Math.max(current.page - 1, 1) }))}
            >
              Previous
            </button>
            <span className="text-sm text-on-surface-variant">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              className="btn-secondary"
              disabled={filters.page >= pagination.totalPages}
              onClick={() => setFilters((current) => ({ ...current, page: current.page + 1 }))}
            >
              Next
            </button>
          </div>
        )}
      </div>

      <EntityDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedDriverId ? selectedDriver?.name || 'Driver' : 'New Driver'}
        subtitle={selectedDriverId ? 'Driver profile, readiness, and scheduling' : 'Create an operational driver record'}
        widthClassName="max-w-4xl"
        footer={
          <>
            {selectedDriverId && (
              <button
                className="btn-secondary text-error"
                disabled={deleteDriverMutation.isLoading}
                onClick={() => {
                  if (window.confirm('Delete this driver? Active assignments must be released first.')) {
                    deleteDriverMutation.mutate(selectedDriverId);
                  }
                }}
              >
                Delete
              </button>
            )}
            <button className="btn-secondary" onClick={() => setDrawerOpen(false)}>Close</button>
            <button
              className="btn-primary"
              disabled={saveDriverMutation.isLoading}
              onClick={() => saveDriverMutation.mutate(form)}
            >
              {saveDriverMutation.isLoading ? 'Saving...' : 'Save Driver'}
            </button>
          </>
        }
      >
        {selectedDriverQuery.isLoading ? (
          <LoadingSpinner className="py-12" />
        ) : (
          <div className="space-y-5">
            <Tabs
              activeTab={drawerTab}
              onChange={(key) => setDrawerTab(key as DriverDrawerTab)}
              tabs={[
                { key: 'profile', label: 'Profile', icon: <UserIcon /> },
                { key: 'credentials', label: 'Credentials', icon: <IdentificationIcon /> },
                { key: 'availability', label: 'Availability', icon: <ClockIcon /> },
                { key: 'blocks', label: 'Blocks', icon: <CalendarDaysIcon />, count: schedule?.scheduleBlocks?.length || 0 },
                { key: 'assignments', label: 'Assignments', icon: <TruckIcon />, count: schedule?.upcomingAssignments?.length || 0 },
              ]}
            />

            {drawerTab === 'profile' && (
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="First name" value={form.firstName} onChange={(value) => setForm({ ...form, firstName: value })} />
                <Field label="Last name" value={form.lastName} onChange={(value) => setForm({ ...form, lastName: value })} />
                <Field label="Email" value={form.email} onChange={(value) => setForm({ ...form, email: value })} />
                <Field label="Phone" value={form.phone} onChange={(value) => setForm({ ...form, phone: value })} />
                <SelectField
                  label="Status"
                  value={form.status}
                  onChange={(value) => setForm({ ...form, status: value })}
                  options={[
                    ['active', 'Active'],
                    ['on_leave', 'On leave'],
                    ['inactive', 'Inactive'],
                  ]}
                />
                <SelectField
                  label="Employment"
                  value={form.employmentType}
                  onChange={(value) => setForm({ ...form, employmentType: value as DriverForm['employmentType'] })}
                  options={[
                    ['full_time', 'Full time'],
                    ['part_time', 'Part time'],
                    ['contractor', 'Contractor'],
                  ]}
                />
                <Field label="Home base" value={form.homeBase} onChange={(value) => setForm({ ...form, homeBase: value })} />
                <Field label="Hourly rate" value={form.hourlyRate} onChange={(value) => setForm({ ...form, hourlyRate: value })} />
                <label className="block md:col-span-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant">Notes</span>
                  <textarea
                    className="input-field mt-1 min-h-[7rem]"
                    value={form.notes}
                    onChange={(event) => setForm({ ...form, notes: event.target.value })}
                  />
                </label>
              </div>
            )}

            {drawerTab === 'credentials' && (
              <div className="space-y-5">
                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="License number" value={form.licenseNumber} onChange={(value) => setForm({ ...form, licenseNumber: value })} />
                  <Field label="License expiry" type="date" value={form.licenseExpiry} onChange={(value) => setForm({ ...form, licenseExpiry: value })} />
                  <Field label="Medical card expiry" type="date" value={form.medicalCardExpiry} onChange={(value) => setForm({ ...form, medicalCardExpiry: value })} />
                  <Field label="Background check expiry" type="date" value={form.backgroundCheckExpiry} onChange={(value) => setForm({ ...form, backgroundCheckExpiry: value })} />
                </div>
                <div className="rounded-lg border border-outline bg-surface-container-lowest p-4">
                  <h3 className="text-sm font-semibold text-on-surface">Vehicle Qualifications</h3>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {vehicleTypes.map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => toggleQualification(type)}
                        className={`rounded-full border px-3 py-1.5 text-sm font-medium ${
                          form.vehicleTypeQualifications.includes(type)
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-outline bg-surface-container-low text-on-surface-variant'
                        }`}
                      >
                        {labelize(type)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {drawerTab === 'availability' && (
              <div className="rounded-lg border border-outline bg-surface-container-lowest">
                <div className="grid grid-cols-[4rem_1fr_1fr_5rem] gap-3 border-b border-outline px-4 py-3 text-xs font-semibold uppercase tracking-wide text-on-surface-variant">
                  <span>Day</span>
                  <span>Start</span>
                  <span>End</span>
                  <span>Active</span>
                </div>
                {form.weeklyAvailability.map((entry) => (
                  <div key={entry.dayOfWeek} className="grid grid-cols-[4rem_1fr_1fr_5rem] gap-3 border-b border-outline/60 px-4 py-3 last:border-b-0">
                    <div className="self-center text-sm font-semibold text-on-surface">{dayLabels[entry.dayOfWeek]}</div>
                    <input className="input-field" type="time" value={entry.startTime} onChange={(event) => updateAvailability(entry.dayOfWeek, { startTime: event.target.value })} />
                    <input className="input-field" type="time" value={entry.endTime} onChange={(event) => updateAvailability(entry.dayOfWeek, { endTime: event.target.value })} />
                    <input className="h-5 w-5 self-center" type="checkbox" checked={entry.active} onChange={(event) => updateAvailability(entry.dayOfWeek, { active: event.target.checked })} />
                  </div>
                ))}
              </div>
            )}

            {drawerTab === 'blocks' && (
              <div className="space-y-4">
                {selectedDriverId ? (
                  <div className="rounded-lg border border-outline bg-surface-container-lowest p-4">
                    <div className="grid gap-3 md:grid-cols-2">
                      <Field label="Start" type="datetime-local" value={blockForm.startAt} onChange={(value) => setBlockForm({ ...blockForm, startAt: value })} />
                      <Field label="End" type="datetime-local" value={blockForm.endAt} onChange={(value) => setBlockForm({ ...blockForm, endAt: value })} />
                      <SelectField
                        label="Type"
                        value={blockForm.blockType}
                        onChange={(value) => setBlockForm({ ...blockForm, blockType: value })}
                        options={[
                          ['time_off', 'Time off'],
                          ['unavailable', 'Unavailable'],
                          ['extra_shift', 'Extra shift'],
                          ['training', 'Training'],
                          ['hold', 'Admin hold'],
                        ]}
                      />
                      <Field label="Notes" value={blockForm.notes} onChange={(value) => setBlockForm({ ...blockForm, notes: value })} />
                    </div>
                    <button
                      className="btn-primary mt-4"
                      disabled={!blockForm.startAt || !blockForm.endAt || createBlockMutation.isLoading}
                      onClick={() => createBlockMutation.mutate()}
                    >
                      Add Block
                    </button>
                  </div>
                ) : (
                  <p className="text-sm text-on-surface-variant">Save the driver before adding schedule blocks.</p>
                )}
                <div className="space-y-2">
                  {(schedule?.scheduleBlocks || []).length ? (
                    schedule.scheduleBlocks.map((block: any) => (
                      <div key={block.id} className="flex items-center justify-between gap-3 rounded-lg border border-outline bg-surface-container-lowest p-3">
                        <div>
                          <div className="font-semibold text-on-surface">{labelize(block.blockType)}</div>
                          <div className="text-xs text-on-surface-variant">
                            {new Date(block.startAt).toLocaleString()} - {new Date(block.endAt).toLocaleString()}
                          </div>
                          {block.notes && <div className="mt-1 text-sm text-on-surface-variant">{block.notes}</div>}
                        </div>
                        <button className="rounded-md p-2 text-error hover:bg-error/10" onClick={() => deleteBlockMutation.mutate(block.id)}>
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ))
                  ) : (
                    <p className="rounded-lg border border-dashed border-outline p-5 text-sm text-on-surface-variant">No schedule blocks recorded.</p>
                  )}
                </div>
              </div>
            )}

            {drawerTab === 'assignments' && (
              <div className="space-y-2">
                {(schedule?.upcomingAssignments || []).length ? (
                  schedule.upcomingAssignments.map((assignment: any) => (
                    <div key={assignment.id} className="rounded-lg border border-outline bg-surface-container-lowest p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="font-semibold text-on-surface">
                            {assignment.reservationRef || assignment.reservationId}
                          </div>
                          <div className="text-sm text-on-surface-variant">
                            {assignment.startDate ? new Date(assignment.startDate).toLocaleString() : 'Date not set'}
                          </div>
                        </div>
                        {assignment.vehicle && (
                          <span className="rounded-full bg-surface-container-low px-3 py-1 text-xs font-semibold text-on-surface-variant">
                            {assignment.vehicle.fleetNumber || assignment.vehicle.model}
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="rounded-lg border border-dashed border-outline p-5 text-sm text-on-surface-variant">No upcoming reservation assignments.</p>
                )}
              </div>
            )}
          </div>
        )}
      </EntityDrawer>
    </PageLayout>
  );
}

function SummaryCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <div className="rounded-lg border border-outline bg-surface-container-lowest p-4">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-surface-container-low text-primary">
          {React.cloneElement(icon as React.ReactElement, { className: 'h-5 w-5' })}
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant">{label}</div>
          <div className="text-2xl font-bold text-on-surface">{value}</div>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
}) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant">{label}</span>
      <input
        className="input-field mt-1"
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<[string, string]>;
}) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant">{label}</span>
      <select className="input-field mt-1" value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map(([optionValue, labelText]) => (
          <option key={optionValue} value={optionValue}>{labelText}</option>
        ))}
      </select>
    </label>
  );
}

export default AdminDrivers;
